/**
 * Coût politique symétrique (ADR 0009, REDTEAM-v02 B1/M2). UNE règle pour tout ce qui déplace de l'argent — mesures,
 * leviers de recettes, rabot, économies par mission, options de crise, mesures estimées :
 *
 *   coût politique annuel = `pc_per_bn_base` × |Md€ de l'année| × sens × `pc_mod_<incidence>`
 *
 * - sens : resserrement (hausse de prélèvement OU économie de dépense, Md€ améliorant le solde) = +1 ;
 *   relâchement (baisse de prélèvement OU dépense nouvelle) = −`pc_loosening_ratio` (popularité de « donner », plus faible en
 *   valeur absolue). Une hausse d'impôt et une économie de même montant coûtent donc exactement la même chose, une baisse
 *   d'impôt et une dépense nouvelle rapportent exactement la même chose ;
 * - incidence (`households` | `firms` | `public`) : un seul modificateur par incidence, appliqué aux DEUX instruments
 *   (prélèvement et dépense) et aux deux sens — les paires de modificateurs sont égales par construction ;
 * - convention de signe des montants : `bn` > 0 = dépense nouvelle ou baisse d'impôt (comme `Measure.cost`).
 * Coût > 0 = capital politique consommé ; < 0 = capital gagné. Aucun coût saisi à la main n'est lu, sauf
 * `political_cost_override` sourcé (source ≠ "TODO").
 */
import { param, paramOpt } from "./params.js";
import { hypTarget, profileAt, profileSteady } from "./profile.js";
import { PCT } from "./state.js";
import type { EventOption, Measure, ParamSet, Profile } from "./types.js";

export type Incidence = "households" | "firms" | "public";

/** Coût politique d'un flux de `bn` Md€ (convention `Measure.cost`) pour une incidence donnée. */
export function politicalCostOfBn(params: ParamSet, bn: number, incidence: Incidence): number {
  if (bn === 0) return 0;
  const base = param(params, "pc_per_bn_base");
  const mod = param(params, `pc_mod_${incidence}`);
  const sense = bn < 0 ? 1 : -param(params, "pc_loosening_ratio");
  return base * Math.abs(bn) * sense * mod;
}

/** Incidence d'une mesure : fiscalité → ménages / entreprises ; sinon ménages si la part qui atteint leur revenu dépasse le seuil. */
export function measureIncidence(params: ParamSet, m: Pick<Measure, "category">): Incidence {
  if (m.category === "fiscalite_menages") return "households";
  if (m.category === "fiscalite_entreprises") return "firms";
  return param(params, `pp_transfer_share_${m.category}`) >= param(params, "pc_household_incidence_threshold") ? "households" : "public";
}

/** Incidence d'un levier de recettes (vocabulaire des leviers, comme engine/src/prices.ts). */
export function leverIncidence(id: string): Incidence {
  if (id === "is") return "firms";
  if (id === "fraude") return "public";
  return "households";
}

/** Incidence d'une économie ciblée sur une mission. */
export function missionIncidence(params: ParamSet, missionId: string): Incidence {
  const share = paramOpt(params, `pp_transfer_share_mission_${missionId}`) ?? 0;
  return share >= param(params, "pc_household_incidence_threshold") ? "households" : "public";
}

const sourcedOverride = (m: Measure): number | undefined =>
  m.political_cost_override && m.political_cost_override.source !== "TODO" ? m.political_cost_override.value : undefined;

/** Coût net d'une mesure l'année k (curseur `cost_leak` appliqué). */
function costAt(m: Measure, k: number, hyp: number | undefined): number {
  let c = profileAt(m.cost, k, m.ramp);
  if (hypTarget(m) === "cost_leak" && hyp !== undefined) c *= 1 - hyp / PCT;
  return c;
}

/** Coût politique d'une mesure l'année k depuis l'adoption (capital consommé cette année-là). */
export function measurePoliticalCostAt(params: ParamSet, m: Measure, k: number, hyp: number | undefined): number {
  if (k < 0) return 0;
  const o = sourcedOverride(m);
  if (o !== undefined) return o * Math.min(1, profileAt(1, k, m.ramp));
  return politicalCostOfBn(params, costAt(m, k, hyp), measureIncidence(params, m));
}

/** Coût politique annuel d'une mesure en régime (affiché sur la fiche de la mesure). */
export function measurePoliticalCost(params: ParamSet, m: Measure, hyp?: number): number {
  const o = sourcedOverride(m);
  if (o !== undefined) return o;
  let c = profileSteady(m.cost);
  const h = hyp ?? m.hyp?.default;
  if (hypTarget(m) === "cost_leak" && h !== undefined) c *= 1 - h / PCT;
  return politicalCostOfBn(params, c, measureIncidence(params, m));
}

/** Profil d'événement à l'année k (tableau fini, scalaire = k = 0 seulement). */
function eventAt(p: Profile, k: number): number {
  if (k < 0) return 0;
  if (Array.isArray(p)) return k < p.length ? (p[k] ?? 0) : 0;
  return k === 0 ? p : 0;
}

/** Coût politique d'une option de crise l'année k depuis son entrée en vigueur (même règle, incidence `public`). */
export function optionPoliticalCostAt(params: ParamSet, o: Pick<EventOption, "cost">, k: number): number {
  return politicalCostOfBn(params, eventAt(o.cost, k), "public");
}

/** Coût politique total d'une option de crise (affiché sur la carte) : coût déclaré hors argent + règle sur chaque année du profil. */
export function optionPoliticalCost(params: ParamSet, o: Pick<EventOption, "cost" | "political_cost">): number {
  const n = Array.isArray(o.cost) ? o.cost.length : 1;
  let t = o.political_cost;
  for (let k = 0; k < n; k++) t += optionPoliticalCostAt(params, o, k);
  return t;
}

/** Montant total d'une option (Σ du profil, Md€) : sert à désigner l'option de référence. */
export function optionTotalCost(o: Pick<EventOption, "cost">): number {
  return Array.isArray(o.cost) ? o.cost.reduce((a, x) => a + x, 0) : o.cost;
}

/** Option de référence d'un événement : `reference_option_id`, sinon la moins coûteuse (première en cas d'égalité). */
export function referenceOption<T extends Pick<EventOption, "id" | "cost">>(event: { id?: string; options: T[]; reference_option_id?: string }): T {
  const options = event.options;
  if (event.reference_option_id !== undefined) {
    const explicit = options.find((o) => o.id === event.reference_option_id);
    if (!explicit) throw new Error(`événement « ${event.id ?? "?"} » : reference_option_id « ${event.reference_option_id} » inconnu`);
    return explicit;
  }
  let best = options[0];
  if (!best) throw new Error("événement sans option");
  for (const o of options) if (optionTotalCost(o) < optionTotalCost(best)) best = o;
  return best;
}
